import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from './icons/Icon';
import apiService from '../services/api';
import { getSelectedAccountId, setSelectedAccountId } from '../services/accountScope';

interface Account {
  _id?: string;
  id?: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended';
}

const idOf = (account: Account) => account._id || account.id || '';

/**
 * Which account the console is acting on.
 *
 * Deliberately says what it does and does not change. Media is scoped for
 * real — a job created under one account is absent from another's list and
 * unreachable by its id — and nothing else is. A switcher that implied
 * otherwise would be worse than none, because the belief it creates is
 * exactly the one that leads someone to treat two accounts as separate when
 * their orders and products are shared.
 */
const AccountSwitcher: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selected, setSelected] = useState<string | null>(getSelectedAccountId());
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiService.getAccounts() as unknown as { accounts: Account[] };
      const list = res.accounts || [];
      setAccounts(list);
      setError(false);

      // Nothing chosen yet. The backend refuses a request that does not name
      // an account, so the console has to pick something — but it picks
      // visibly, and the current account is always on screen rather than
      // implied.
      if (!getSelectedAccountId() && list.length) {
        const first = idOf(list[0]);
        setSelectedAccountId(first);
        setSelected(first);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  const choose = (account: Account) => {
    const id = idOf(account);
    if (id === selected) { setOpen(false); return; }

    setSelectedAccountId(id);
    setSelected(id);
    setOpen(false);

    // A full reload rather than a broadcast every panel has to subscribe to.
    // Blunt, and correct: every section refetches, and there is no window
    // where one panel shows the previous account's data because it happened
    // not to be listening.
    window.location.reload();
  };

  const current = accounts.find((account) => idOf(account) === selected);

  if (error) {
    return (
      <span className="text-xs text-ink-3" title="Could not load accounts">
        no account
      </span>
    );
  }

  if (!accounts.length) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg border border-border-default px-2.5 py-1.5 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
      >
        <Icon name="customers" className="h-4 w-4 text-ink-3" />
        <span className="hidden whitespace-nowrap sm:inline">{current ? current.name : 'Choose account'}</span>
        <span className="sr-only sm:hidden">Account: {current ? current.name : 'none'}</span>
        <Icon name="chevronDown" className="h-3.5 w-3.5 text-ink-3" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1.5 w-72 overflow-hidden rounded-lg border border-border-default bg-surface py-1 shadow-popover"
        >
          {accounts.map((account) => {
            const id = idOf(account);
            const isCurrent = id === selected;
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => choose(account)}
                className={`flex w-full items-center justify-between px-3 py-2 text-left transition-colors ${
                  isCurrent ? 'bg-surface-hover' : 'hover:bg-surface-hover'
                }`}
              >
                <span>
                  <span className="block text-sm font-medium text-ink-1">{account.name}</span>
                  <span className="block text-xs text-ink-3">{account.slug}</span>
                </span>
                {account.status !== 'active' && (
                  <span className="text-xs text-ink-3">{account.status}</span>
                )}
              </button>
            );
          })}

          {/* The honest part. Without it this control looks like isolation. */}
          <p className="mt-1 border-t border-border-default px-3 pt-2 text-xs text-ink-3">
            Switching changes <span className="font-bold">Media</span> only. Orders,
            products, affiliates and posts are shared across every account.
          </p>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;
